/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { menuApi } from '../../services/menuApi';

interface MenuState {
  currentMenu: any | null;
  nextMenu: any | null;
  slotOptions: any[];
  recommendations: any[];
  mySelections: any[];
  skips: any[];
  loading: boolean;
  error: string | null;
  selectedDate: string; // YYYY-MM-DD
  selectedSlot: string | null;
}

const initialState: MenuState = {
  currentMenu: null,
  nextMenu: null,
  slotOptions: [],
  recommendations: [],
  mySelections: [],
  skips: [],
  loading: false,
  error: null,
  selectedDate: new Date().toISOString().split('T')[0],
  selectedSlot: null,
};

// Async thunks
export const fetchCurrentMenu = createAsyncThunk(
  'weeklyMenu/fetchCurrentMenu',
  async (_, { rejectWithValue }) => {
    try {
      const response = await menuApi.getCurrentMenu();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.detail || 'Failed to fetch current menu'
      );
    }
  }
);

export const fetchNextMenu = createAsyncThunk(
  'weeklyMenu/fetchNextMenu',
  async (_, { rejectWithValue }) => {
    try {
      const response = await menuApi.getNextMenu();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.detail || 'Failed to fetch next menu'
      );
    }
  }
);

// Normalize a WeeklyMenuItem API response into a flat object the card components expect
function normalizeMenuItem(item: any): any {
  const meal = item.meal || {};
  const recipe = meal.recipe || {};
  return {
    ...item,
    // Flat fields the cards read directly
    name: meal.name || recipe.name || item.name || 'Unknown Meal',
    description: meal.description || recipe.description || item.description || '',
    calories: meal.calories || recipe.calories || 0,
    protein: meal.protein_g || recipe.protein_g || 0,
    carbs: meal.carbohydrates_g || recipe.carbohydrates_g || 0,
    fat: meal.fat_g || recipe.fat_g || 0,
    fiber: meal.fiber_g || recipe.fiber_g || 0,
    image_url: meal.image_url || recipe.image_url || null,
    price: item.price_override ?? item.effective_price ?? meal.base_price ?? null,
    is_vegetarian: meal.is_vegetarian ?? recipe.is_vegetarian ?? false,
    cuisine_type: meal.cuisine_type || recipe.cuisine_type || '',
    tags: meal.tags || recipe.tags || [],
    // Keep nested refs intact
    meal,
    recipe,
  };
}

export const fetchSlotOptions = createAsyncThunk(
  'weeklyMenu/fetchSlotOptions',
  async ({ date, slot }: { date: string; slot: string }, { rejectWithValue }) => {
    try {
      const response = await menuApi.getSlotOptions(date, slot);
      return (response.data || []).map(normalizeMenuItem);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.detail || 'Failed to fetch slot options'
      );
    }
  }
);

export const fetchRecommendations = createAsyncThunk(
  'weeklyMenu/fetchRecommendations',
  async ({ date, slot }: { date: string; slot: string }, { rejectWithValue }) => {
    try {
      const response = await menuApi.getRecommended(date, slot);
      // Recommendations may be an array or { items: [] }
      const items = Array.isArray(response.data) ? response.data : (response.data?.items || response.data?.recommendations || []);
      return items.map((item: any) => ({
        ...normalizeMenuItem(item.menu_item || item),
        score: Math.round((item.overall_score || item.score || item.match_score || 0) * 100),
        explanation: item.explanation || item.reason || '',
        tags: item.tags || [],
      }));
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.detail || 'Failed to fetch recommendations'
      );
    }
  }
);

export const createSelection = createAsyncThunk(
  'weeklyMenu/createSelection',
  async (
    data: { menu_item_id: number; special_instructions?: string; customizations?: any[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await menuApi.createSelection(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.detail || 'Failed to create selection'
      );
    }
  }
);

export const updateSelection = createAsyncThunk(
  'weeklyMenu/updateSelection',
  async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await menuApi.updateSelection(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.detail || 'Failed to update selection'
      );
    }
  }
);

export const deleteSelection = createAsyncThunk(
  'weeklyMenu/deleteSelection',
  async (id: number, { rejectWithValue }) => {
    try {
      await menuApi.deleteSelection(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.detail || 'Failed to delete selection'
      );
    }
  }
);

export const fetchMySelections = createAsyncThunk(
  'weeklyMenu/fetchMySelections',
  async (weekOffset?: number, { rejectWithValue }: any = {}) => {
    try {
      const response = await menuApi.getMySelections(weekOffset);
      const data = response.data;
      // Backend returns { days: [{ date, breakfast, lunch, dinner, snack, skips }] }
      // Flatten into a list of { date, slot, ...selectionData } for easy lookup
      const slots = ['breakfast', 'lunch', 'dinner', 'snack'];
      const selections: any[] = [];
      for (const day of data.days || []) {
        for (const slot of slots) {
          if (day[slot]) {
            selections.push({ ...day[slot], date: day.date, slot });
          }
        }
      }
      return selections;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.detail || 'Failed to fetch selections'
      );
    }
  }
);

export const skipMeal = createAsyncThunk(
  'weeklyMenu/skipMeal',
  async (data: { skip_date: string; slot?: string; reason?: string }, { rejectWithValue }) => {
    try {
      const response = await menuApi.skipMeal(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.detail || 'Failed to skip meal'
      );
    }
  }
);

export const fetchSkips = createAsyncThunk(
  'weeklyMenu/fetchSkips',
  async (_, { rejectWithValue }) => {
    try {
      const response = await menuApi.getSkips();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.detail || 'Failed to fetch skips'
      );
    }
  }
);

// Slice
const weeklyMenuSlice = createSlice({
  name: 'weeklyMenu',
  initialState,
  reducers: {
    setSelectedDate: (state, action: PayloadAction<string>) => {
      state.selectedDate = action.payload;
    },
    setSelectedSlot: (state, action: PayloadAction<string | null>) => {
      state.selectedSlot = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch current menu
    builder.addCase(fetchCurrentMenu.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchCurrentMenu.fulfilled, (state, action) => {
      state.loading = false;
      state.currentMenu = action.payload;
    });
    builder.addCase(fetchCurrentMenu.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch next menu
    builder.addCase(fetchNextMenu.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchNextMenu.fulfilled, (state, action) => {
      state.loading = false;
      state.nextMenu = action.payload;
    });
    builder.addCase(fetchNextMenu.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch slot options
    builder.addCase(fetchSlotOptions.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSlotOptions.fulfilled, (state, action) => {
      state.loading = false;
      state.slotOptions = action.payload;
    });
    builder.addCase(fetchSlotOptions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch recommendations
    builder.addCase(fetchRecommendations.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchRecommendations.fulfilled, (state, action) => {
      state.loading = false;
      state.recommendations = action.payload;
    });
    builder.addCase(fetchRecommendations.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create selection
    builder.addCase(createSelection.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createSelection.fulfilled, (state, action) => {
      state.loading = false;
      state.mySelections.push(action.payload);
    });
    builder.addCase(createSelection.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update selection
    builder.addCase(updateSelection.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateSelection.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.mySelections.findIndex((s: any) => s.id === action.payload.id);
      if (index !== -1) {
        state.mySelections[index] = action.payload;
      }
    });
    builder.addCase(updateSelection.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete selection
    builder.addCase(deleteSelection.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteSelection.fulfilled, (state, action) => {
      state.loading = false;
      state.mySelections = state.mySelections.filter((s: any) => s.id !== action.payload);
    });
    builder.addCase(deleteSelection.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch my selections
    builder.addCase(fetchMySelections.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMySelections.fulfilled, (state, action) => {
      state.loading = false;
      state.mySelections = action.payload;
    });
    builder.addCase(fetchMySelections.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Skip meal
    builder.addCase(skipMeal.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(skipMeal.fulfilled, (state, action) => {
      state.loading = false;
      state.skips.push(action.payload);
    });
    builder.addCase(skipMeal.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch skips
    builder.addCase(fetchSkips.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSkips.fulfilled, (state, action) => {
      state.loading = false;
      state.skips = action.payload;
    });
    builder.addCase(fetchSkips.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { setSelectedDate, setSelectedSlot, clearError } = weeklyMenuSlice.actions;
export default weeklyMenuSlice.reducer;
